import {
  useDisclosure,
  Flex,
  Button,
  Box,
  Text,
  Heading,
  Tooltip,
} from '@chakra-ui/react';
import {
  CompositionMaterial,
  compositionApi,
} from '@texas/api/endpoints/compositionApi';
import { useTranslation } from 'react-i18next';
import { VerifyDialogWithRequest } from '../../dialog/VerifyDialogWithRequest';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { GetColorWithType } from '../../colorPicker/ColorPicker';
import { RemoveComponent } from '../../RemoveComponent';
import { ColorDot } from '../../colorPicker/ColorDot';
import { ColorBox } from '../../colorPicker/ColorBox';

type State = 'hidden';
type ColorComponentType = 'dots' | 'detailed';

interface ComponentSettings {
  metaList?: State;
  colors?: State;
  colorComponent?: ColorComponentType;
  isVdc?: boolean;
}

export function CompositionMaterialView({
  material,
  onClick,
  onRemoved,
  settings,
}: {
  material: CompositionMaterial;
  onClick: () => void;
  onRemoved?: () => void;
  settings?: ComponentSettings;
}) {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
      <RemoveComponent
        onClick={onRemoved && !settings?.isVdc ? onOpen : undefined}
      >
        <Flex
          animation={fadeInScaleAnimation()}
          flexDirection="column"
          bg="texas.bg.800"
          border="1px solid"
          borderColor="texas.bg.800"
          _light={{ bg: 'gray.10' }}
          p={4}
          gap={2}
          role="group"
          borderRadius="md"
          maxW="500px"
        >
          <Tooltip label={t('general.clickToEdit')}>
            <Button
              boxSize="full"
              onClick={() => onClick()}
              variant="unstyled"
              _hover={{ opacity: 0.7, transform: 'scale(1.02)' }}
              textAlign="start"
            >
              <Flex flexDir="column" gap={2}>
                <Box>
                  <Heading fontSize="lg" whiteSpace="normal">
                    {material.material.text}
                  </Heading>
                  <Text
                    overflowWrap="anywhere"
                    noOfLines={3}
                    fontSize="sm"
                    fontWeight="normal"
                    color="gray.300"
                    css={{ textWrap: 'wrap' }}
                  >
                    {material.note}
                  </Text>
                  {settings?.metaList !== 'hidden' && (
                    <>
                      <PrintMetaData
                        texts={material.techniques.map((x) => x.text)}
                        summaryLabel={t('configuration.techniques')}
                      />
                      <PrintMetaData
                        texts={material.treatments.map((x) => x.text)}
                        summaryLabel={t('configuration.treatments')}
                      />
                      <PrintMetaData
                        texts={material.qualities.map((x) =>
                          x.value ? `${x.text}: ${x.value}` : x.text,
                        )}
                        summaryLabel={t('configuration.qualities')}
                      />
                    </>
                  )}
                </Box>

                {settings?.colors !== 'hidden' && (
                  <Flex gap={2} flexWrap="wrap">
                    {material.colors.length === 0 && (
                      <Text variant="sub" fontWeight="normal" fontSize="sm">
                        {t('colors.noColors')}
                      </Text>
                    )}
                    {material.colors.map((x) => {
                      const color =
                        x.colorType === null ? GetColorWithType(x) : x;

                      if (settings?.colorComponent === 'detailed') {
                        return <ColorBox key={color.name} color={color} />;
                      }

                      return (
                        <ColorDot
                          key={color.name}
                          hex={color.hex}
                          name={color.name}
                          code={color.code}
                        />
                      );
                    })}
                  </Flex>
                )}
              </Flex>
            </Button>
          </Tooltip>
        </Flex>
      </RemoveComponent>
      {onRemoved && (
        <VerifyDialogWithRequest
          headerTitle={t('materials.remove')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={compositionApi.removeCompositionMaterial}
          args={[material.id]}
          isOpen={isOpen}
          onClose={onClose}
          onPerformed={onRemoved}
          onSuccessTitle={t('general.successfullyRemoved')}
        >
          {t('alert.areYouSure')}
        </VerifyDialogWithRequest>
      )}
    </>
  );
}

function PrintMetaData({
  texts,
  summaryLabel,
}: {
  texts: string[];
  summaryLabel: string;
}) {
  const length = texts.length;

  if (length === 0) return null;

  if (length >= 3) {
    return (
      <Text fontSize="sm" fontWeight="normal">
        {length} {summaryLabel}
      </Text>
    );
  }

  return (
    <Flex flexWrap="wrap">
      {texts.map((x) => (
        <Text key={x} fontSize="sm" fontWeight="normal" pr={4}>
          {x}
        </Text>
      ))}
    </Flex>
  );
}
